import React, { createContext, useState, useContext, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { useUserRole } from "./UserRoleContext";

// Create the UserPersona context
const UserPersonaContext = createContext();

/**
 * UserPersonaProvider Component
 *
 * Provides persona management functionality throughout the application
 * Supports dynamic UI adaptation based on user personas within roles
 * Persists persona selection in local storage
 */
export const UserPersonaProvider = ({ children }) => {
  // Get authenticated user, role, and auth state
  const auth = useAuth();
  const user = auth?.user;
  const { role } = useUserRole();
  const [loading, setLoading] = useState(true);

  // Define available personas for each role
  const availablePersonas = {
    fronter: [
      {
        id: "new_fronter",
        name: "New Fronter",
        description: "Guided lead qualification workflows",
      },
      {
        id: "experienced_fronter",
        name: "Experienced Fronter",
        description: "Advanced qualification techniques",
      },
      {
        id: "high_volume",
        name: "High Volume",
        description: "Optimized for maximum call efficiency",
      },
      {
        id: "quality_focused",
        name: "Quality Focused",
        description: "Emphasis on lead quality over quantity",
      },
    ],
    closer: [
      {
        id: "new_closer",
        name: "New Closer",
        description: "Guided deal closing workflows",
      },
      {
        id: "experienced_closer",
        name: "Experienced Closer",
        description: "Advanced negotiation techniques",
      },
      {
        id: "deal_maximizer",
        name: "Deal Maximizer",
        description: "Focus on maximizing deal value",
      },
      {
        id: "relationship_builder",
        name: "Relationship Builder",
        description: "Long-term customer relationship focus",
      },
    ],
    agent: [
      {
        id: "new_agent",
        name: "New Agent",
        description: "Simplified interface with guided workflows",
      },
      {
        id: "experienced_agent",
        name: "Experienced Agent",
        description: "Advanced features and shortcuts",
      },
      {
        id: "sales_focused",
        name: "Sales Focused",
        description: "Optimized for conversion metrics",
      },
      {
        id: "support_focused",
        name: "Support Focused",
        description: "Focused on customer satisfaction",
      },
    ],
    manager: [
      {
        id: "team_leader",
        name: "Team Leader",
        description: "Focus on team performance and coaching",
      },
      {
        id: "operations_manager",
        name: "Operations Manager",
        description: "Campaign and resource optimization",
      },
      {
        id: "analytics_manager",
        name: "Analytics Manager",
        description: "Deep insights and reporting",
      },
    ],
    admin: [
      {
        id: "system_admin",
        name: "System Administrator",
        description: "Technical configuration and maintenance",
      },
      {
        id: "executive",
        name: "Executive",
        description: "High-level overview and business metrics",
      },
    ],
  };

  // Initialize persona state from localStorage or default to first persona for role
  const [persona, setPersona] = useState(() => {
    const savedPersona = localStorage.getItem("userPersona");
    if (savedPersona) {
      return savedPersona;
    }

    // Default to first persona for the current role
    return availablePersonas[role]?.[0]?.id || "default";
  });

  // Update persona when role changes to ensure it's valid for the new role
  useEffect(() => {
    const currentPersonas = availablePersonas[role] || [];
    const isValidPersona = currentPersonas.some((p) => p.id === persona);

    if (!isValidPersona && currentPersonas.length > 0) {
      // If current persona is not valid for this role, switch to first available
      updatePersona(currentPersonas[0].id);
    }

    setLoading(false);
  }, [role, persona]);

  // Update persona and save to localStorage
  const updatePersona = (newPersona) => {
    const currentPersonas = availablePersonas[role] || [];
    const isValidPersona = currentPersonas.some((p) => p.id === newPersona);

    if (isValidPersona) {
      setPersona(newPersona);
      localStorage.setItem("userPersona", newPersona);
      return true;
    }
    return false;
  };

  // Get current persona details
  const getCurrentPersona = () => {
    const currentPersonas = availablePersonas[role] || [];
    return currentPersonas.find((p) => p.id === persona) || null;
  };

  // Get all available personas for current role
  const getAvailablePersonas = () => {
    return availablePersonas[role] || [];
  };

  // Define persona-specific UI preferences
  const personaPreferences = {
    // Fronter personas
    new_fronter: {
      simplifiedUI: true,
      showGuidedTours: true,
      defaultView: "list",
      showHelpTips: true,
      dashboardMetrics: ["calls", "connected", "qualified"],
      primaryAction: "start_calling",
    },
    experienced_fronter: {
      simplifiedUI: false,
      showGuidedTours: false,
      defaultView: "grid",
      showHelpTips: false,
      dashboardMetrics: [
        "calls",
        "talk_time",
        "qualification_rate",
        "appointments",
      ],
      primaryAction: "start_calling",
    },
    high_volume: {
      simplifiedUI: true,
      showGuidedTours: false,
      defaultView: "list",
      showHelpTips: false,
      dashboardMetrics: ["calls", "connected", "talk_time", "efficiency"],
      primaryAction: "start_calling",
    },
    quality_focused: {
      simplifiedUI: false,
      showGuidedTours: false,
      defaultView: "grid",
      showHelpTips: true,
      dashboardMetrics: [
        "qualification_rate",
        "lead_quality",
        "appointments",
        "conversion",
      ],
      primaryAction: "view_leads",
    },

    // Closer personas
    new_closer: {
      simplifiedUI: true,
      showGuidedTours: true,
      defaultView: "list",
      showHelpTips: true,
      dashboardMetrics: ["appointments", "deals", "conversion"],
      primaryAction: "view_appointments",
    },
    experienced_closer: {
      simplifiedUI: false,
      showGuidedTours: false,
      defaultView: "grid",
      showHelpTips: false,
      dashboardMetrics: ["deals", "deal_value", "conversion", "upsell_rate"],
      primaryAction: "view_deals",
    },
    deal_maximizer: {
      simplifiedUI: false,
      showGuidedTours: false,
      defaultView: "grid",
      showHelpTips: false,
      dashboardMetrics: [
        "avg_deal_size",
        "deal_value",
        "upsell_rate",
        "margin",
      ],
      primaryAction: "view_deals",
    },
    relationship_builder: {
      simplifiedUI: false,
      showGuidedTours: false,
      defaultView: "grid",
      showHelpTips: true,
      dashboardMetrics: [
        "customer_satisfaction",
        "retention",
        "referrals",
        "deals",
      ],
      primaryAction: "view_customers",
    },

    // Agent personas (legacy)
    new_agent: {
      simplifiedUI: true,
      showGuidedTours: true,
      defaultView: "list",
      showHelpTips: true,
      dashboardMetrics: ["calls", "connected", "conversion"],
      primaryAction: "start_calling",
    },
    experienced_agent: {
      simplifiedUI: false,
      showGuidedTours: false,
      defaultView: "grid",
      showHelpTips: false,
      dashboardMetrics: ["calls", "talk_time", "conversion", "deals"],
      primaryAction: "start_calling",
    },
    sales_focused: {
      simplifiedUI: false,
      showGuidedTours: false,
      defaultView: "grid",
      showHelpTips: false,
      dashboardMetrics: ["conversion", "deals", "deal_value", "calls"],
      primaryAction: "view_leads",
    },
    support_focused: {
      simplifiedUI: false,
      showGuidedTours: false,
      defaultView: "list",
      showHelpTips: false,
      dashboardMetrics: ["talk_time", "satisfaction", "resolution", "calls"],
      primaryAction: "view_tickets",
    },

    // Manager personas
    team_leader: {
      simplifiedUI: false,
      showGuidedTours: false,
      defaultView: "grid",
      showHelpTips: false,
      dashboardMetrics: [
        "team_performance",
        "agent_activity",
        "conversion_rate",
      ],
      primaryAction: "team_overview",
    },
    operations_manager: {
      simplifiedUI: false,
      showGuidedTours: false,
      defaultView: "grid",
      showHelpTips: false,
      dashboardMetrics: [
        "active_campaigns",
        "resource_utilization",
        "efficiency",
      ],
      primaryAction: "campaign_management",
    },
    analytics_manager: {
      simplifiedUI: false,
      showGuidedTours: false,
      defaultView: "grid",
      showHelpTips: false,
      dashboardMetrics: [
        "conversion_trends",
        "performance_analysis",
        "forecasts",
      ],
      primaryAction: "analytics_dashboard",
    },

    // Admin personas
    system_admin: {
      simplifiedUI: false,
      showGuidedTours: false,
      defaultView: "grid",
      showHelpTips: false,
      dashboardMetrics: ["system_health", "user_accounts", "security"],
      primaryAction: "system_settings",
    },
    executive: {
      simplifiedUI: true,
      showGuidedTours: false,
      defaultView: "grid",
      showHelpTips: false,
      dashboardMetrics: [
        "conversion_rate",
        "revenue",
        "agent_efficiency",
        "cost_per_call",
      ],
      primaryAction: "executive_dashboard",
    },
  };

  // Get preferences for current persona
  const getPersonaPreferences = () => {
    return personaPreferences[persona] || personaPreferences.default;
  };

  return (
    <UserPersonaContext.Provider
      value={{
        persona,
        updatePersona,
        getCurrentPersona,
        getAvailablePersonas,
        getPersonaPreferences,
        loading,
      }}
    >
      {children}
    </UserPersonaContext.Provider>
  );
};

// Custom hook to use the UserPersona context
export const useUserPersona = () => {
  const context = useContext(UserPersonaContext);
  if (!context) {
    throw new Error("useUserPersona must be used within a UserPersonaProvider");
  }
  return context;
};

export default UserPersonaContext;
